// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { Linking, Pressable, StyleSheet, View, type LayoutChangeEvent } from 'react-native';
import Svg, { Line, Path, Rect } from 'react-native-svg';

import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';

import { buildJobLogsUrl } from './jobLogsUrl';
import type { AnalysisStepJob } from './useAnalysisSteps';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * The jobs of one run, one lane each, on a shared clock.
 *
 * A run is a round of jobs now — a detection per provider and detector, the canopy, the
 * elevations, the hole pictures — dispatched and answered on their own, on whichever worker
 * was free. The per-provider stage bars below this say what each detection did inside; this
 * says how the round went as a whole: which jobs ran side by side, which one waited for its
 * imagery, which one the map was decided after, and which is still out.
 *
 * Each lane is an ordinary row — the job's name, the track, its status — with only the track
 * drawn as SVG, sized in pixels from the width the container reports. The first cut scaled a
 * whole fixed-size drawing to the screen, and on a wide monitor the labels became headlines
 * and the status column fell off the right edge; text as text ellipsises and keeps the page's
 * type, and the track alone is what needs to be a picture.
 *
 * Time runs left to right from the run's start to its end (or to now, while it is open). A
 * lane's bar is the job's dispatched→finished span; a dotted stub before it is the wait
 * between planning and dispatch. A diamond at the end of a lane marks a job whose answer the
 * course was decided from — the moment the map changed.
 */

const TRACK_HEIGHT = 20;
const STATUS_WIDTH = 160;
const LOGS_WIDTH = 44;
const MIN_BAR = 4;
const FALLBACK_WIDTH = 680;
const GAP = 8;

const toneOf = (status: string): string => {
  switch (status) {
    case 'succeeded':
      return appColors.status.success;
    case 'partial':
      return appColors.status.warning;
    case 'failed':
      return appColors.status.danger;
    case 'dispatched':
      return appColors.accent.primary;
    default:
      return appColors.text.muted;
  }
};

/** Local time of day with seconds — the resolution a log line has; the date when it is not today. */
const clock = (iso: string): string => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const today = new Date();
  const sameDay =
    parsed.getFullYear() === today.getFullYear() && parsed.getMonth() === today.getMonth() && parsed.getDate() === today.getDate();
  return sameDay ? parsed.toLocaleTimeString() : parsed.toLocaleString();
};

const parse = (iso: string | null): number | null => {
  if (!iso) return null;
  const millis = Date.parse(iso);
  return Number.isNaN(millis) ? null : millis;
};

export const jobLaneLabel = (job: AnalysisStepJob, t: Translate): string =>
  [
    t(`courseAnalysis.status.jobKind.${job.kind}`),
    job.provider,
    job.detector ? (job.modelVersion ? `${job.detector}@${job.modelVersion}` : job.detector) : null,
  ]
    .filter(Boolean)
    .join(' · ');

export function AnalysisJobSwimlane({
  jobs,
  startedAt,
  finishedAt,
  now = Date.now(),
  t,
}: {
  jobs: AnalysisStepJob[];
  /** The run's start — the left edge of every lane. */
  startedAt: string;
  /** The run's end; null while it is still open, in which case `now` is the right edge. */
  finishedAt: string | null;
  now?: number;
  t: Translate;
}) {
  // Measured once the view has laid out; until then (and in tests, which never lay out)
  // the fallback width keeps the drawing sensible.
  const [measured, setMeasured] = useState(0);
  const onLayout = (event: LayoutChangeEvent) => {
    const next = Math.floor(event.nativeEvent.layout.width);
    if (next > 0 && next !== measured) setMeasured(next);
  };

  if (jobs.length === 0) return null;

  const width = measured > 0 ? measured : FALLBACK_WIDTH;
  const labelWidth = Math.min(420, Math.max(180, Math.round(width * 0.36)));
  const trackWidth = Math.max(60, width - labelWidth - STATUS_WIDTH - LOGS_WIDTH - GAP * 3);

  const start = parse(startedAt) ?? Math.min(...jobs.map((job) => parse(job.requestedAt) ?? now));
  const ends = jobs.map((job) => parse(job.finishedAt) ?? parse(job.dispatchedAt) ?? start);
  const end = Math.max(parse(finishedAt) ?? 0, ...ends, finishedAt ? 0 : now, start + 1000);
  const span = Math.max(1, end - start);
  const x = (millis: number) => ((Math.min(Math.max(millis, start), end) - start) / span) * trackWidth;

  return (
    <View style={styles.wrap} testID="analysis-flow-swimlane" onLayout={onLayout}>
      <Text style={styles.heading}>{t('analysisFlowPage.jobsHeading', { count: jobs.length })}</Text>

      {jobs.map((job) => {
        const tone = toneOf(job.status);
        const requested = parse(job.requestedAt) ?? start;
        const dispatched = parse(job.dispatchedAt);
        const finished = parse(job.finishedAt);
        const barFrom = dispatched ?? (finished ?? null);
        const barTo = finished ?? (dispatched ? (finishedAt ? end : now) : null);
        const waitTo = dispatched ?? finished ?? (finishedAt ? end : now);
        const status = [
          t(`courseAnalysis.status.jobStatus.${job.status}`),
          job.status === 'planned' && job.waitingOn
            ? t('courseAnalysis.status.jobWaitingOn', { source: job.waitingOn })
            : null,
          job.workerSeconds !== null ? t('batchFleetPage.stepsSeconds', { seconds: job.workerSeconds }) : null,
          job.attempt > 1 ? t('analysisFlowPage.laneAttempt', { attempt: job.attempt }) : null,
        ]
          .filter(Boolean)
          .join(' · ');

        return (
          <View key={job.id} style={styles.laneBlock}>
          <View style={styles.lane}>
            <Text style={[styles.label, { width: labelWidth }]} numberOfLines={1} testID="analysis-flow-job-lane">
              {jobLaneLabel(job, t)}
            </Text>
            <Svg width={trackWidth} height={TRACK_HEIGHT}>
              <Line x1={0.5} y1={0} x2={0.5} y2={TRACK_HEIGHT} stroke={appColors.border.default} strokeWidth={1} />
              <Line
                x1={trackWidth - 0.5}
                y1={0}
                x2={trackWidth - 0.5}
                y2={TRACK_HEIGHT}
                stroke={appColors.border.default}
                strokeWidth={1}
                strokeDasharray={finishedAt ? undefined : '3 3'}
              />
              {/* Planned but not yet dispatched: the wait, dotted. */}
              {waitTo > requested ? (
                <Line
                  x1={x(requested)}
                  y1={TRACK_HEIGHT / 2}
                  x2={x(waitTo)}
                  y2={TRACK_HEIGHT / 2}
                  stroke={appColors.text.muted}
                  strokeWidth={1}
                  strokeDasharray="2 3"
                />
              ) : null}
              {barFrom !== null && barTo !== null ? (
                <Rect
                  x={x(barFrom)}
                  y={3}
                  width={Math.max(MIN_BAR, x(barTo) - x(barFrom))}
                  height={TRACK_HEIGHT - 6}
                  rx={3}
                  fill={tone}
                  fillOpacity={job.status === 'dispatched' ? 0.25 : 0.45}
                  stroke={tone}
                  strokeOpacity={0.9}
                />
              ) : null}
              {job.decided && finished !== null ? (
                <Path
                  d={`M${Math.min(x(finished), trackWidth - 6)} 2 l6 8 l-6 8 l-6 -8 z`}
                  fill={appColors.accent.primary}
                  stroke={appColors.accent.primary}
                  testID="analysis-flow-job-decided"
                />
              ) : null}
            </Svg>
            <Text style={styles.status} numberOfLines={1}>
              {status}
            </Text>
            {/* The job's own log lines in Kibana — every service that touched it stamps the
                job id, so this is the answer to "and what did it do" and "why did it fail". */}
            <Pressable
              accessibilityRole="link"
              accessibilityLabel={t('batchFleetPage.jobLogsLink')}
              testID="analysis-flow-job-logs"
              onPress={() => Linking.openURL(buildJobLogsUrl(job.id))}
            >
              <Text style={styles.logs}>{t('courseAnalysis.status.jobLogs')}</Text>
            </Pressable>
          </View>
          {/* The lane's own clock, to line up against a log line: planned, dispatched, finished. */}
          <Text style={[styles.laneClock, { marginLeft: labelWidth + GAP }]} numberOfLines={1} testID="analysis-flow-job-clock">
            {[
              t('analysisFlowPage.lanePlanned', { time: clock(job.requestedAt) }),
              job.dispatchedAt ? t('analysisFlowPage.laneDispatched', { time: clock(job.dispatchedAt) }) : null,
              job.finishedAt ? t('analysisFlowPage.laneFinished', { time: clock(job.finishedAt) }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
          {job.error ? (
            // Why it failed, where the lane is — "Failed" on its own sends the admin to Kibana
            // for a sentence the job row already carries.
            <Text style={[styles.laneError, { marginLeft: labelWidth + GAP }]} numberOfLines={3} testID="analysis-flow-job-error">
              {job.error}
            </Text>
          ) : null}
          </View>
        );
      })}

      <View style={styles.axisRow}>
        <View style={{ width: labelWidth + GAP }} />
        <Text style={[styles.axis, { width: trackWidth }]} numberOfLines={1}>
          {finishedAt
            ? t('batchFleetPage.stepsSeconds', { seconds: Math.round(span / 1000) })
            : t('analysisFlowPage.laneOpen')}
        </Text>
      </View>
      <Text style={styles.legend}>{t('analysisFlowPage.laneLegend')}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: appColors.border.default,
    padding: 12,
    marginBottom: 12,
    gap: 6,
    overflow: 'hidden',
  },
  heading: { color: appColors.text.primary, fontWeight: '600', fontSize: 15, marginBottom: 4 },
  laneBlock: { gap: 2 },
  lane: { flexDirection: 'row', alignItems: 'center', gap: GAP },
  laneClock: { color: appColors.text.muted, fontSize: 11 },
  laneError: { color: appColors.status.danger, fontSize: 12 },
  label: { color: appColors.text.primary, fontSize: 13 },
  status: { color: appColors.text.secondary, fontSize: 12, width: STATUS_WIDTH },
  logs: { color: appColors.accent.primary, fontSize: 12, width: LOGS_WIDTH, textDecorationLine: 'underline' },
  axisRow: { flexDirection: 'row', alignItems: 'center' },
  axis: { color: appColors.text.muted, fontSize: 11, textAlign: 'right' },
  legend: { color: appColors.text.muted, fontSize: 11, marginTop: 2 },
});
